/**
 * ./src/modules/shopping-list/components/AddToShoppingListButtonRegular.tsx
 *
 * AddToShoppingListButtonRegular component
 *
 * @copyright DesignShop, 2024
 * @since 10/18/2024
 */
import { Button, ButtonProps } from '@/modules/common/components/Button';
import { Heart, HeartSolid } from '@/modules/common/components/icons';
import { cn } from '@/modules/common/lib/utils';

import { ShoppingListSaveType, useShoppingListActions } from '../hooks/useShoppingListActions';
import { AddToShoppingListButtonProps } from './AddToShoppingListButton';

export type AddToShoppingListButtonPdpProps = ButtonProps & AddToShoppingListButtonProps;

export const AddToShoppingListButtonRegular = (props: AddToShoppingListButtonPdpProps) => {
  const {
    className,
    sku,
    quantity = 1,
    textLineItem,
    image,
    color,
    variant = 'primary',
    saveType = ShoppingListSaveType.Product,
    callbacks,
  } = props;

  const { isOnShoppingList, handleAdd, handleRemove, isLoading } = useShoppingListActions({
    sku,
    quantity,
    textLineItem,
    image,
    color,
    type: saveType,
    callbacks,
  });

  const HeartIcon = isOnShoppingList ? HeartSolid : Heart;
  const buttonLabel = isOnShoppingList ? 'Saved' : 'Save';
  const heartIconClasses = cn({ 'animate-heartbeat': isLoading });

  return (
    <Button
      aria-label={buttonLabel}
      variant={variant}
      className={cn('disabled:text-black', className)}
      onClick={isOnShoppingList ? handleRemove : handleAdd}
      disabled={isLoading}
      type="button"
      data-testid="add-to-shopping-list-button"
      id="add-to-shopping-list-button"
    >
      <HeartIcon className={heartIconClasses} size="small" data-testid="heart-icon-desktop" />
      <span data-testid="button-label">{buttonLabel}</span>
    </Button>
  );
};
